import React, { useState } from 'react';
import './AuthModal.css';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

interface AuthModalProps {
  // No props needed - all state is managed by AuthContext
}

const AuthModal: React.FC<AuthModalProps> = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    businessName: '',
    businessType: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { 
    isModalOpen, 
    modalMode, 
    closeAuthModal, 
    openAuthModal, 
    signIn, 
    signUp, 
    signUpVendor
  } = useAuth();

  // Use context values instead of props
  const isOpenModal = isModalOpen;
  const currentMode = modalMode;

  if (!isOpenModal) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await authService.forgotPassword(formData.email);
      setSuccessMessage(
        result.message ||
          'If an account exists for that email, we sent a password reset link. Check your inbox.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (currentMode === 'login') {
        await signIn(formData.email, formData.password);
      } else if (currentMode === 'vendor-signup') {
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        await signUpVendor(formData.email, formData.password, formData.firstName, formData.lastName, formData.businessName, formData.businessType);
      } else {
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        await signUp(formData.email, formData.password, formData.firstName, formData.lastName);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    // Use the context's openAuthModal to switch modes
    let newMode: 'login' | 'signup' | 'vendor-signup' = 'login';
    if (currentMode === 'login') {
      newMode = 'signup';
    } else if (currentMode === 'signup' || currentMode === 'vendor-signup') {
      newMode = 'login';
    }
    openAuthModal(newMode);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      businessName: '',
      businessType: ''
    });
    setError('');
    setSuccessMessage('');
    setShowForgotPassword(false);
  };

  const openForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowForgotPassword(true);
    setError('');
    setSuccessMessage('');
  };

  const backToLogin = () => {
    setShowForgotPassword(false);
    setError('');
    setSuccessMessage('');
  };

  return (
    <div className="auth-modal-overlay" onClick={closeAuthModal}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close stagger-animate stagger-1" onClick={closeAuthModal}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>

        <div className="auth-modal-content">
          <div className="auth-header stagger-animate stagger-2">
            <h2 className="auth-title">
              {showForgotPassword
                ? 'Forgot password'
                : currentMode === 'login'
                ? 'Welcome Back'
                : currentMode === 'vendor-signup'
                ? 'Become A Vendor'
                : 'Join Worldasia'}
            </h2>
            <p className="auth-subtitle">
              {showForgotPassword
                ? 'Enter your email and we will send you a reset link'
                : currentMode === 'login'
                ? 'Sign in to access your account and continue shopping'
                : currentMode === 'vendor-signup'
                ? 'Register your business and start selling on Worldasia'
                : 'Create an account to start shopping and save your preferences'}
            </p>
          </div>

          {error && (
            <div className="auth-error stagger-animate stagger-3">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="auth-success stagger-animate stagger-3">
              {successMessage}
            </div>
          )}

          {showForgotPassword ? (
            <form className="auth-form" onSubmit={handleForgotPassword}>
              <div className="form-group stagger-animate stagger-4">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email address"
                  disabled={isLoading}
                />
              </div>
              <button type="submit" className="auth-submit-btn stagger-animate stagger-5" disabled={isLoading}>
                {isLoading ? 'Sending…' : 'Send reset link'}
              </button>
              <button
                type="button"
                className="auth-switch-btn forgot-back-btn"
                onClick={backToLogin}
                disabled={isLoading}
              >
                Back to sign in
              </button>
            </form>
          ) : (
          <form className="auth-form" onSubmit={handleSubmit}>
            {(currentMode === 'signup' || currentMode === 'vendor-signup') && (
              <div className="form-row stagger-animate stagger-3">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required={currentMode === 'signup' || currentMode === 'vendor-signup'}
                    placeholder="Enter your first name"
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required={currentMode === 'signup' || currentMode === 'vendor-signup'}
                    placeholder="Enter your last name"
                    disabled={isLoading}
                  />
                </div>
              </div>
            )}

            {currentMode === 'vendor-signup' && (
              <>
                <div className="form-group stagger-animate stagger-3">
                  <label htmlFor="businessName">Business Name</label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    required={currentMode === 'vendor-signup'}
                    placeholder="Enter your business name"
                    disabled={isLoading}
                  />
                </div>
                <div className="form-group stagger-animate stagger-3">
                  <label htmlFor="businessType">Business Type</label>
                  <select
                    id="businessType"
                    name="businessType"
                    value={formData.businessType}
                    onChange={(e) => handleInputChange(e as any)}
                    required={currentMode === 'vendor-signup'}
                    disabled={isLoading}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '14px' }}
                  >
                    <option value="">Select business type</option>
                    <option value="clothing">Clothing & Fashion</option>
                    <option value="electronics">Electronics</option>
                    <option value="food">Food & Beverages</option>
                    <option value="beauty">Beauty & Personal Care</option>
                    <option value="home">Home & Living</option>
                    <option value="services">Services</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </>
            )}

            <div className="form-group stagger-animate stagger-4">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email address"
                disabled={isLoading}
              />
            </div>

            <div className="form-group stagger-animate stagger-5">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Enter your password"
                disabled={isLoading}
              />
            </div>

            {(currentMode === 'signup' || currentMode === 'vendor-signup') && (
              <div className="form-group stagger-animate stagger-6">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required={currentMode === 'signup' || currentMode === 'vendor-signup'}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                />
              </div>
            )}

            {currentMode === 'login' && (
              <div className="form-options stagger-animate stagger-6">
                <label className="checkbox-label">
                  <input type="checkbox" />
                  <span className="checkmark"></span>
                  Remember me
                </label>
                <button type="button" className="forgot-password" onClick={openForgotPassword}>
                  Forgot password?
                </button>
              </div>
            )}

            <button type="submit" className="auth-submit-btn stagger-animate stagger-7" disabled={isLoading}>
              {isLoading ? 'Loading...' : (currentMode === 'login' ? 'Sign In' : currentMode === 'vendor-signup' ? 'Register as Vendor' : 'Create Account')}
            </button>
          </form>
          )}

          {/* Temporarily hidden - will be enabled later with Google/Facebook OAuth setup */}
          {/* <div className="auth-divider stagger-animate stagger-7">
            <span>or</span>
          </div>

          <div className="social-auth stagger-animate stagger-8">
            <button 
              type="button"
              className="social-auth-btn google" 
              disabled={isLoading}
              onClick={handleGoogleSignIn}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
            <button 
              type="button"
              className="social-auth-btn facebook" 
              disabled={isLoading}
              onClick={handleFacebookSignIn}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Continue with Facebook
            </button>
          </div> */}

          <div className="auth-footer stagger-animate stagger-8">
            {!showForgotPassword && (
            <p className="auth-switch-text">
              {currentMode === 'login' ? "Don't have an account?" : "Already have an account?"}
              <button className="auth-switch-btn" onClick={switchMode} disabled={isLoading}>
                {currentMode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
            )}
            {currentMode === 'vendor-signup' && (
              <p className="auth-switch-text" style={{ marginTop: '8px' }}>
                Want to join as a customer?
                <button className="auth-switch-btn" onClick={() => openAuthModal('signup')} disabled={isLoading}>
                  Customer sign up
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal; 